import { err, ok } from 'neverthrow';
import { SpecialForm } from '../types';
import { CompileOutput, ParseError, ParseRes } from '../../eraql-ast/ast';
import { tryTypeCheckExpr } from './helpers';
import { c } from '../combinators';
import { AST } from '../../../ast';
import { Assert } from '../../../utils';
import { WithMeta } from '../../lex';
import { compileAst } from '../../eraql-ast/compile';
import { DateToTimestamp, Today, Yesterday } from '../../../builder/utilities';

const case_: SpecialForm = (args, ctx, func) =>
  (ctx.eager ? eagerCase_ : lazyCase_)(args, ctx, func);

const eagerCase_: SpecialForm = (args, ctx, func) => {
  const argsRes = c.ArgsWithOpts(
    [
      c.ListOf(
        c.StructOf({
          required: { when: c.EraqlAstOrItem, then: c.EraqlAstOrItem },
          optional: {},
          strict: true,
        })
      ),
    ] as const,
    c.StructOf({
      required: { else: c.EraqlAstOrItem },
      optional: {},
      strict: true,
    })
  )(args, ctx, func);

  if (argsRes.isErr()) {
    return err(argsRes.error);
  }

  const [cases, opts] = argsRes.value;

  for (const [{ when, then }, _meta] of cases[0]) {
    const whenRes = c.ExprOfTy('boolean')(when.v[0], ctx);

    if (whenRes.isErr()) {
      return err(whenRes.error);
    }

    if (whenRes.value.evaluate() === true) {
      return compileAst(then.v[0], ctx);
    }
  }

  if (opts?.else !== undefined) {
    return compileAst(opts.else.v[0], ctx);
  } else {
    // we’ll probs want to include a “type me as” field on special
    // forms to allow the eager evaluator to return the correclty typed
    // null without actually invoking the special form
    const e: ParseError = {
      t: 'custom',
      msg: `Untyped else clause, this will eventually be handled by the compiler, but in the short run write an "else" clause in this "case"`,
    };
    return err([[e, func[1]]]);
  }
};

const lazyCase_: SpecialForm = (args, ctx, func) =>
  c
    .ArgsWithOpts(
      [
        c.ListOf(
          c.StructOf({
            required: { when: c.Expr, then: c.Expr },
            optional: {},
            strict: true,
          })
        ),
      ] as const,
      c.StructOf({
        required: { else: c.Expr },
        optional: {},
        strict: true,
      })
    )(args, ctx, func)
    .andThen(([[branches], else_]): ParseRes<CompileOutput> => {
      const expr: AST._Case<AST.ExprMacroChildren> = {
        t: 'case',
        cases: branches.map(([{ when, then }, _meta]) => ({
          when: when.v[0].ast,
          then: then.v[0].ast,
        })),
        else: else_ ? else_.else.v[0].ast : undefined,
      };

      return tryTypeCheckExpr(expr, func[1]);
    });

const cast: SpecialForm = (args, ctx, func) =>
  c
    .Args(c.Expr, c.Ty)(args, ctx, func)
    .andThen(([expr, targetTy]) => {
      const cast: AST._Cast<AST.ExprMacroChildren> = {
        t: 'cast',
        targetTy: targetTy.ty,
        expr: expr.ast,
      };

      return tryTypeCheckExpr(cast, func[1]);
    });

const if_: SpecialForm = (args, ctx, func) =>
  (ctx.eager ? eagerIf_ : lazyIf_)(args, ctx, func);

const eagerIf_: SpecialForm = (args, ctx, func) =>
  c
    .Args(
      c.IncludeMeta(c.ExprOfTy('boolean')),
      c.StructOf({
        required: { then: c.EraqlAstOrItem },
        optional: { else: c.EraqlAstOrItem },
        strict: true,
      })
    )(args, ctx, func)
    .andThen(
      ([[cond, _condMeta], { then, else: else_ }]): ParseRes<CompileOutput> => {
        const condRes = cond.tryEvaluate();

        if (condRes.isErr()) {
          throw new Error(
            `TODO: Eager trying to eval something it cant yet "${cond.toEraQL()}"`
          );
        }

        if (condRes.value === true) {
          return compileAst(then.v[0], ctx);
        } else {
          if (else_ !== undefined) {
            return compileAst(else_.v[0], ctx);
          } else {
            // we’ll probs want to include a “type me as” field on special
            // forms to allow the eager evaluator to return the correclty typed
            // null without actually invoking the special form
            const e: ParseError = {
              t: 'custom',
              msg: `Untyped else clause, this will eventually be handled by the compiler, but in the short run write an "else" clause in this "if"`,
            };
            return err([[e, func[1]]]);
          }
        }
      }
    );

const lazyIf_: SpecialForm = (args, ctx, func) =>
  c
    .Args(
      c.Expr,
      c.StructOf({
        required: { then: c.Expr },
        optional: { else: c.Expr },
        strict: true,
      })
    )(args, ctx, func)
    .andThen(([cond, { then, else: else_ }]): ParseRes<CompileOutput> => {
      const expr: AST._Case<AST.ExprMacroChildren> = {
        t: 'case',
        cases: [{ when: cond.ast, then: then.v[0].ast }],
        else: else_ ? else_.v[0].ast : undefined,
      };

      return tryTypeCheckExpr(expr, func[1]);
    });

const get: SpecialForm = (args, ctx, func) =>
  c
    .Args(c.Expr, c.StringLit)(args, ctx, func)
    .andThen(([target, field]): ParseRes<CompileOutput> => {
      const expr: AST._GetField<AST.ExprMacroChildren> = {
        t: 'get-field',
        name: field,
        expr: target.ast,
      };

      return tryTypeCheckExpr(expr, func[1]);
    });

const categorize: SpecialForm = ([logic, ...rest], ctx, func) => {
  Assert.assert(
    logic !== undefined && rest.length === 0,
    'We already checked arity'
  );

  const logics = c.ListOf(c.List)(logic, ctx);

  if (logics.isErr()) {
    return err(logics.error);
  }

  const logicBranches: {
    category: string;
    expr: AST.ExprFR;
    filter: AST.ExprFR | null;
  }[] = [];

  for (const [branch, _branchMeta] of logics.value) {
    const res = c.ArgsWithOpts(
      [c.StringLit, c.Expr] as const,
      c.StructOf({
        required: { filter: c.Expr },
        optional: {},
        strict: true,
      })
    )(branch, ctx, null);

    if (res.isErr()) {
      return err(res.error);
    }

    const [[category, expr], opts] = res.value;

    logicBranches.push({
      category,
      expr: expr.ast,
      filter: opts ? opts.filter.v[0].ast : null,
    });
  }

  const expr: AST._Categorize<AST.ExprMacroChildren> = {
    t: 'categorize',
    logic: logicBranches,
  };

  return tryTypeCheckExpr(expr, func[1]);
};

const ExprVar_: SpecialForm = (args, ctx, func) =>
  c
    .Args(
      c.StructOf({
        required: { name: c.StringLit, scope: c.StringLit, ty: c.Ty },
        optional: { default: c.Expr },
        strict: true,
      })
    )(args, ctx, func)
    .andThen(
      ([{ name, scope, ty, default: default_ }]): ParseRes<CompileOutput> => {
        const expr: AST._ExprVar<AST.ExprMacroChildren> = {
          t: 'expr-var',
          name: name.v[0],
          scope: scope.v[0],
          ty: ty.v[0],
          default: default_ ? default_.v[0].ast : null,
        };

        return tryTypeCheckExpr(expr, func[1]);
      }
    );

const pick: SpecialForm = (...args) =>
  c
    .Args(
      c.ItemMap,
      c.ListOf(c.StringLit)
    )(...args)
    .andThen(([items, list]) => {
      const output: Record<string, CompileOutput> = {};
      const missingKeys: WithMeta<string>[] = [];
      for (const [key, meta] of list) {
        const item = items[key];
        if (item === undefined) {
          missingKeys.push([key, meta]);
        } else {
          output[key] = item;
        }
      }

      const res: CompileOutput = {
        item: {
          t: 'item-map',
          map: output,
        },
      };

      return missingKeys.length === 0
        ? ok(res)
        : err(
            missingKeys.map(
              ([key, meta]): WithMeta<ParseError> => [
                { t: 'missing-key', key },
                meta,
              ]
            )
          );
    });

const except: SpecialForm = (...args) =>
  c
    .Args(
      c.ItemMap,
      c.ListOf(c.StringLit)
    )(...args)
    .andThen(([items, list]) => {
      const output: Record<string, CompileOutput> = { ...items };
      const missingKeys: WithMeta<string>[] = [];
      for (const [key, meta] of list) {
        const item = output[key];
        if (item === undefined) {
          missingKeys.push([key, meta]);
        } else {
          delete output[key];
        }
      }

      const res: CompileOutput = {
        item: {
          t: 'item-map',
          map: output,
        },
      };

      return missingKeys.length === 0
        ? ok(res)
        : err(
            missingKeys.map(
              ([key, meta]): WithMeta<ParseError> => [
                { t: 'missing-key', key },
                meta,
              ]
            )
          );
    });

const ordering =
  (direction: AST.SortDirection): SpecialForm =>
  (ast, ctx, func) =>
    c
      .Args(c.Expr)(ast, ctx, func)
      .map(([expr]) => ({
        item: { t: 'order-by', expr, direction },
        bindings: {},
      }));

const today: SpecialForm = (_ast, _ctx, _func) =>
  ok({ item: { t: 'expr', expr: Today() }, bindings: {} });

const yesterday: SpecialForm = (_ast, _ctx, _func) =>
  ok({ item: { t: 'expr', expr: Yesterday() }, bindings: {} });

const dateToTimestamp: SpecialForm = (ast, ctx, func) =>
  c
    .Args(c.Expr)(ast, ctx, func)
    .map(([dateStr]) => ({
      item: { t: 'expr', expr: DateToTimestamp(dateStr) },
      bindings: {},
    }));

export const EXPR_SPECIAL_FORMS: Record<
  string,
  [artiy: number | number[] | 'any', fn: SpecialForm]
> = {
  yesterday: [0, yesterday],
  today: [0, today],
  date_to_timestamp: [1, dateToTimestamp],
  pick: [[2], pick],
  except: [[2], except],
  ExprVar: [[1], ExprVar_],
  categorize: [1, categorize],
  case: [[1, 2], case_],
  cast: [2, cast],
  if: [2, if_],
  get: [2, get],
  asc: [1, ordering('asc')],
  desc: [1, ordering('desc')],
};
